// src/pages/events/EventsList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Stack, Chip, Button, IconButton,
  TextField, InputAdornment, MenuItem, Tabs, Tab, CircularProgress,
  useMediaQuery, useTheme, Pagination, alpha,
} from "@mui/material";
import {
  EventNote, Search, FilterList, LiveTv, AccessTime,
  PlayCircle, History, Add, LocationOn, People,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

const C = {
  primary: "#0A2E8A", primaryDark: "#061E5C", primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC", primarySofter: "#F2F5FE",
  secondary: "#10682A", secondaryDark: "#0A4A1C", secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC", paper: "#FFFFFF",
  border: "#E5E9F0", borderSoft: "#EEF1F6",
  textPrimary: "#0D1117", textSecondary: "#5B6B7E", textMuted: "#8A9BB0",
  error: "#DC2626", errorSoft: "#FEE2E2",
  live: "#DC2626", // for "LIVE NOW" indicator
};

const PLATFORM_LABELS = {
  "zoom": "Zoom",
  "google-meet": "Google Meet",
  "youtube-live": "YouTube Live",
  "microsoft-teams": "Microsoft Teams",
  "in-person": "In Person",
  "other": "Other",
};

const PLATFORM_COLORS = {
  "zoom": "#2D8CFF",
  "google-meet": "#00897B",
  "youtube-live": "#FF0000",
  "microsoft-teams": "#5059C9",
  "in-person": "#10682A",
  "other": "#6B7280",
};

const CATEGORIES = [
  { value: "all",          label: "All Categories" },
  { value: "general",      label: "General" },
  { value: "workshop",     label: "Workshop" },
  { value: "webinar",      label: "Webinar" },
  { value: "ama",          label: "AMA / Q&A" },
  { value: "panel",        label: "Panel Discussion" },
  { value: "networking",   label: "Networking" },
  { value: "career",       label: "Career" },
];

// Tab constants
const TAB_UPCOMING = 0;
const TAB_LIVE     = 1;
const TAB_PAST     = 2;

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API}/${path.replace(/\\/g, "/").replace(/^\//, "")}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const timeUntil = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "";
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days > 0)  return `in ${days}d`;
  if (hours > 0) return `in ${hours}h`;
  return `in ${mins}m`;
};

// ── Event Card ───────────────────────────────────────────────────────────────
const EventCard = ({ event, onOpen }) => {
  const liveStatus = event.computedStatus || "upcoming";
  const isLive = liveStatus === "live";
  const isPast = liveStatus === "ended";
  const platformColor = PLATFORM_COLORS[event.platform] || C.textSecondary;

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen(event)}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        bgcolor: C.paper,
        cursor: "pointer",
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex", flexDirection: "column",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 10px 30px ${C.primary}15`,
          borderColor: C.primary,
        },
      }}
    >
      {/* Cover image / gradient fallback */}
      <Box sx={{
        height: 140,
        position: "relative",
        background: event.coverImage
          ? `linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.4)), url(${resolveImageUrl(event.coverImage)})`
          : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        {!event.coverImage && (
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: 180, height: 180, borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
          }} />
        )}

        {/* LIVE badge — pulses */}
        {isLive && (
          <Box sx={{
            position: "absolute", top: 12, left: 12, zIndex: 1,
            bgcolor: C.live, color: "#fff",
            fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
            px: 1, py: 0.4, borderRadius: 1,
            display: "flex", alignItems: "center", gap: 0.5,
            boxShadow: `0 2px 8px ${C.live}50`,
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.04)" },
            },
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />
            LIVE NOW
          </Box>
        )}

        {/* Past event overlay */}
        {isPast && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 1,
            bgcolor: "rgba(13,17,23,0.45)",
            display: "flex", alignItems: "flex-end", justifyContent: "flex-start",
            p: 1.5,
          }}>
            <Chip
              icon={<History sx={{ fontSize: "13px !important", color: "#fff !important" }} />}
              label="Ended"
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.6)", color: "#fff", fontWeight: 700,
                fontSize: 10, height: 22,
              }}
            />
          </Box>
        )}

        {/* Platform chip — top right */}
        <Chip
          label={PLATFORM_LABELS[event.platform] || "Event"}
          size="small"
          sx={{
            position: "absolute", top: 12, right: 12,
            bgcolor: platformColor, color: "#fff",
            fontWeight: 700, fontSize: 10, height: 22,
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        />
      </Box>

      {/* Card body */}
      <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Category tag */}
        {event.category && event.category !== "general" && (
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: 1.2,
            textTransform: "uppercase", color: C.primary, mb: 0.75,
          }}>
            {event.category}
          </Typography>
        )}

        <Typography sx={{
          fontWeight: 800, fontSize: 16, color: C.textPrimary, lineHeight: 1.3,
          mb: 1, letterSpacing: -0.2,
          overflow: "hidden", display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}>
          {event.title}
        </Typography>

        <Typography sx={{
          fontSize: 12.5, color: C.textSecondary, lineHeight: 1.5, mb: 1.5,
          overflow: "hidden", display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          flex: 1,
        }}>
          {event.description}
        </Typography>

        {/* Date + count up time */}
        <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
          <AccessTime sx={{ fontSize: 14, color: C.textMuted }} />
          <Typography sx={{ fontSize: 11.5, color: C.textSecondary, fontWeight: 600 }}>
            {formatDateTime(event.startTime)}
          </Typography>
          {!isPast && !isLive && (
            <Chip
              label={timeUntil(event.startTime)}
              size="small"
              sx={{
                ml: "auto !important",
                bgcolor: C.primarySoft, color: C.primary,
                fontSize: 10, height: 18, fontWeight: 700,
              }}
            />
          )}
        </Stack>

        {/* Host + attendee count footer */}
        <Stack direction="row" alignItems="center" spacing={1} mt={1}
          sx={{ pt: 1, borderTop: `1px solid ${C.borderSoft}` }}>
          <Box sx={{
            width: 22, height: 22, borderRadius: "50%",
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            color: "#fff", fontSize: 10, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {(event.hostName || "?").charAt(0).toUpperCase()}
          </Box>
          <Typography sx={{ fontSize: 11, color: C.textSecondary, fontWeight: 600, flex: 1 }} noWrap>
            {event.hostName || "Host"}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.4}>
            <People sx={{ fontSize: 13, color: C.textMuted }} />
            <Typography sx={{ fontSize: 11, color: C.textMuted, fontWeight: 700 }}>
              {event.attendeeCount || event.attendees?.length || 0}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function EventsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tabValue, setTabValue] = useState(TAB_UPCOMING);
  const [category, setCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const userRole = (user?.role || "").toLowerCase();
  const canHost = userRole === "mentor" || userRole === "superuser";

  // Map tab to backend status param
  const statusForTab = (t) => {
    if (t === TAB_LIVE) return "live";
    if (t === TAB_PAST) return "past";
    return "upcoming";
  };

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {
        status: statusForTab(tabValue),
        page,
        limit: 12,
      };
      if (category !== "all") params.category = category;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (userRole) params.userRole = userRole;

      const res = await axios.get(`${API}/api/events`, { params });
      setEvents(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to load events.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tabValue, category, searchQuery, page, userRole]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [tabValue, category, searchQuery]);

  const openEvent = (ev) => navigate(`/events/${ev._id}`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3, maxWidth: 1280, mx: "auto", width: "100%" }}>
        {/* ── Hero header ──────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
              <EventNote sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
              }}>
                Live & Upcoming
              </Typography>
            </Stack>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
              color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
            }}>
              Events
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 14 }, maxWidth: 580, lineHeight: 1.5,
            }}>
              Workshops, webinars, AMAs, and networking sessions — join via Zoom, Google Meet, YouTube Live, or in person.
            </Typography>

            {canHost && (
              <Button
                startIcon={<Add />}
                onClick={() => navigate("/events/new")}
                sx={{
                  mt: 2,
                  bgcolor: "#fff", color: C.primary,
                  fontWeight: 800, fontSize: 13,
                  textTransform: "none", borderRadius: 2.5,
                  px: 2.5, py: 1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.92)", boxShadow: "0 6px 16px rgba(0,0,0,0.2)" },
                }}
              >
                Create Event
              </Button>
            )}
          </Box>
        </Paper>

        {/* ── Filter row: tabs + search + category ──────────── */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 }, mb: 2.5, borderRadius: 3,
            border: `1px solid ${C.border}`, bgcolor: C.paper,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                minHeight: 38,
                "& .MuiTab-root": {
                  textTransform: "none", fontWeight: 700, fontSize: 13,
                  minHeight: 38, color: C.textSecondary, px: 1.5,
                },
                "& .MuiTabs-indicator": { bgcolor: C.primary, height: 3 },
                "& .Mui-selected": { color: `${C.primary} !important` },
              }}
            >
              <Tab icon={<AccessTime sx={{ fontSize: 16 }} />} iconPosition="start" label="Upcoming" />
              <Tab icon={<LiveTv sx={{ fontSize: 16 }} />} iconPosition="start" label="Live Now" />
              <Tab icon={<History sx={{ fontSize: 16 }} />} iconPosition="start" label="Past" />
            </Tabs>

            <Box sx={{ flex: 1, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  flex: 1, minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, bgcolor: C.primarySofter, fontSize: 13,
                    "& fieldset": { borderColor: C.border },
                    "&:hover fieldset": { borderColor: C.primary },
                    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: C.textMuted }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                size="small"
                sx={{
                  minWidth: 170,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, bgcolor: C.primarySofter, fontSize: 13,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterList sx={{ fontSize: 18, color: C.textMuted }} />
                    </InputAdornment>
                  ),
                }}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value} sx={{ fontSize: 13 }}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>
        </Paper>

        {/* ── Event grid ───────────────────────────────────── */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: C.primary }} />
          </Box>
        ) : error ? (
          <Paper elevation={0} sx={{
            p: 6, textAlign: "center", borderRadius: 3,
            border: `1px solid ${C.errorSoft}`, bgcolor: C.errorSoft,
          }}>
            <Typography sx={{ color: C.error, fontWeight: 700 }}>{error}</Typography>
          </Paper>
        ) : events.length === 0 ? (
          <Paper elevation={0} sx={{
            p: { xs: 4, sm: 6 }, textAlign: "center", borderRadius: 3,
            border: `1px solid ${C.border}`, bgcolor: C.paper,
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2,
              boxShadow: `0 8px 20px ${C.primary}30`,
            }}>
              <EventNote sx={{ fontSize: 36, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, fontSize: 16 }}>
              {tabValue === TAB_UPCOMING && "No upcoming events"}
              {tabValue === TAB_LIVE && "Nothing live right now"}
              {tabValue === TAB_PAST && "No past events"}
            </Typography>
            <Typography sx={{ color: C.textSecondary, fontSize: 13 }}>
              {tabValue === TAB_LIVE
                ? "Check back later — live events appear here while they're happening."
                : "Check back soon or adjust your filters."}
            </Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
              },
              gap: 2.5,
              mb: 3,
            }}>
              {events.map((ev) => (
                <EventCard key={ev._id} event={ev} onOpen={openEvent} />
              ))}
            </Box>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root.Mui-selected": {
                      bgcolor: C.primary, color: "#fff",
                      "&:hover": { bgcolor: C.primaryDark },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Footer />
    </Box>
  );
}